"use client";

import { useEffect, useState } from "react";

import { isStoreDealActive } from "./store-deal-utils";

// setTimeout overflows above ~24.8 days; such deals cannot expire in-session.
const MAX_TIMEOUT_MS = 2_147_483_647;

/**
 * Hydration-safe {@link isStoreDealActive} for server-rendered store rows.
 *
 * Best pages are ISR-cached, so a deal active at render time may have expired
 * by the time the visitor loads the page. The first render therefore matches
 * the server (deal present -> shown); after mount the deal is re-checked
 * against the client clock and hidden once its endTime passes.
 */
export function useStoreDealActive(
  deal: { endTime?: Date } | null | undefined,
): boolean {
  const [active, setActive] = useState(deal != null);

  useEffect(() => {
    const isActive = isStoreDealActive(deal);
    setActive(isActive);
    const endMs = deal?.endTime?.getTime();
    if (!isActive || endMs == null) return;

    const remainingMs = endMs - Date.now();
    if (remainingMs > MAX_TIMEOUT_MS) return;
    const timeout = setTimeout(() => setActive(false), remainingMs);
    return () => clearTimeout(timeout);
  }, [deal]);

  return active;
}
